import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../../store';
import { useAssistant } from '../../contexts/AssistantContext';
import { useAuth, getHomeRoute } from '../../features/auth';
import type { BreadcrumbItemConfig, UseBreadcrumbConfig } from './types';

/**
 * Hook personalizado para construir breadcrumbs automáticamente
 * Maneja la lógica de navegación incluyendo permisos (/home vs /modules)
 *
 * @param config - Configuración del breadcrumb
 * @returns Array de items para el breadcrumb
 *
 * @example
 * // Breadcrumb simple: Control Center > Assistant > Metrics
 * const items = useBreadcrumb({
 *   section: 'Metrics',
 *   includeAssistant: true
 * });
 *
 * @example
 * // Con subsección: Control Center > Assistant > Evaluations > Questions
 * const items = useBreadcrumb({
 *   section: 'Evaluations',
 *   subsection: 'Questions',
 *   includeAssistant: true
 * });
 *
 * @example
 * // Sin asistente: Control Center > Create Agent
 * const items = useBreadcrumb({
 *   section: 'Create Agent',
 *   includeAssistant: false
 * });
 */
export const useBreadcrumb = (config: UseBreadcrumbConfig): BreadcrumbItemConfig[] => {
  const {
    section,
    subsection,
    subsectionDynamic,
    includeAssistant = true,
    customAssistantName,
    customAssistantPath,
    additionalItems = [],
    onSectionClick
  } = config;

  const navigate = useNavigate();
  const params = useParams();
  const { currentAccount } = useAccount();
  const { currentAssistant } = useAssistant();
  const { hasPermission } = useAuth();

  const items = useMemo(() => {
    const breadcrumbItems: BreadcrumbItemConfig[] = [];
    const currentAccountId = currentAccount?.id;
    const assistantId = params.AssistantID || currentAssistant?.id;
    const assistantName = customAssistantName || currentAssistant?.name || 'Agent';

    // 1. Control Center - siempre primero
    if (currentAccountId) {
      breadcrumbItems.push({
        label: 'Control Center',
        onClick: () => navigate(getHomeRoute(currentAccountId, hasPermission)),
        isActive: false,
      });
    } else {
      breadcrumbItems.push({
        label: 'Control Center',
        isActive: false,
      });
    }

    // 2. Assistant Name - segundo nivel (opcional)
    if (includeAssistant) {
      const assistantPath = customAssistantPath ||
        (currentAccountId && assistantId ? `/accounts/${currentAccountId}/assistants/${assistantId}/inbox` : undefined);

      breadcrumbItems.push({
        label: assistantName,
        onClick: assistantPath ? () => navigate(assistantPath) : undefined,
        isActive: false,
      });
    }

    // 3. Sección principal
    const isSectionActive = !subsection && !subsectionDynamic && additionalItems.length === 0;
    breadcrumbItems.push({
      label: section,
      isActive: isSectionActive,
      // Si la sección no es activa y se proporciona onSectionClick, hacerla clickeable
      onClick: !isSectionActive && onSectionClick ? onSectionClick : undefined,
    });

    // 4. Subsección estática (si existe)
    if (subsection) {
      breadcrumbItems.push({
        label: subsection,
        isActive: !subsectionDynamic && additionalItems.length === 0,
      });
    }

    // 5. Subsección dinámica (si existe)
    if (subsectionDynamic) {
      breadcrumbItems.push({
        label: subsectionDynamic,
        isActive: additionalItems.length === 0,
      });
    }

    // 6. Items adicionales personalizados
    if (additionalItems.length > 0) {
      additionalItems.forEach((item, index) => {
        breadcrumbItems.push({
          ...item,
          // El último item adicional es el activo
          isActive: index === additionalItems.length - 1,
        });
      });
    }

    return breadcrumbItems;
  }, [
    section,
    subsection,
    subsectionDynamic,
    includeAssistant,
    customAssistantName,
    customAssistantPath,
    additionalItems,
    onSectionClick,
    currentAccount,
    currentAssistant,
    params.AssistantID,
    navigate,
    hasPermission,
  ]);

  return items;
};
